import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { dataAccessEnvironment } from 'data-access';
import {
  BehaviorSubject,
  forkJoin,
  interval,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { checkTrialDateExpire } from 'src/app/_helpers/check-trial-account-expiration';
import { WebToolsMenu } from 'src/app/_models/WebTools';
import { environment } from '../../../environments/environment';
import { Token } from '../../_models/authws/authws';
import { LOGIN_PATH_PREFIXES } from '../models/login-path-prefix';
import { AccountType, UserInfo } from '../models/user';
import { updateCompanySettings } from 'src/app/route-profiles/helper/ams.repository';
import { Common } from '@trimblemaps/trimblemaps-js';

const defaultOptions = { headers: { 'content-type': 'application/json' } };
@Injectable()
export class AuthenticationService {
  currentUser: Observable<UserInfo>;
  productName: string;
  private currentUserSubject: BehaviorSubject<UserInfo>;
  private authWsUrl: string;
  private refreshTokenSubscription: Subscription;

  constructor(private http: HttpClient, private router: Router) {
    this.currentUserSubject = new BehaviorSubject<UserInfo>(
      JSON.parse(localStorage.getItem('currentUser'))
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.productName = 'PCMilerWebsite'; // TODO: extract to config file
    this.authWsUrl = `${environment.authwsUrl}identity/v2/token`;
  }

  get currentUserValue(): UserInfo {
    return this.currentUserSubject.value;
  }

  setCurrentUser(user: UserInfo): void {
    // store user details and access token in local storage to keep user logged in between page refreshes
    user.expiresAt = new Date();
    user.loggedInAt = new Date();
    user.expiresAt.setHours(
      new Date().getHours() + environment.tokenExpiryTime
    );
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
    dataAccessEnvironment.authToken = user.authToken;
    updateCompanySettings({
      units:
        user?.region?.toLowerCase() === Common.Region.EU
          ? 'Metric'
          : 'Imperial',
    });
  }

  refreshToken(user: UserInfo) {
    return this.http
      .post<Token>(this.authWsUrl, {
        refreshToken: user?._jwtToken?.refresh_token,
      })
      .pipe(
        map((refreshToken: Token) => {
          user._jwtToken = refreshToken;
          this.setCurrentUser(user);
          return user;
        })
      );
  }

  getJwtToken(authenticationToken: string): Observable<Token> {
    return this.http.post<Token>(this.authWsUrl, {
      authenticationToken,
    });
  }

  authenticateWithRefreshToken() {
    const user: UserInfo = JSON.parse(localStorage.getItem('currentUser'));
    const expires = user._jwtToken?.expires_in || 3600; // 21600 or 3600 secs // 6 or 1 hrs
    this.refreshTokenSubscription = interval(expires * 1000) // ms
      .pipe(
        map(() => {
          const refreshToken = user._jwtToken?.refresh_token;
          return this.http.post<Token>(this.authWsUrl, { refreshToken });
        })
      )
      .subscribe((resp) => {
        resp.subscribe(
          (token: Token) => {
            user._jwtToken = token;
            this.setCurrentUser(user);
          },
          (err) => {
            // Todo: where to display error message.
          }
        );
      });
  }

  getUserInfo(
    userName: string,
    accountName: string,
    authToken: string
  ): Observable<[any, Token]> {
    const userInfoUrl = `${environment.pcmilerAppUrl}v1/user`;
    const basicAuthHeader = `${userName}:${accountName}:${authToken}`;
    const encodedHeader = `Basic ${window.btoa(basicAuthHeader)}`;
    return forkJoin([
      this.http.get(userInfoUrl, {
        headers: { Authorization: encodedHeader },
      }),
      this.getJwtToken(authToken),
    ]);
  }

  getHTMLContent(externalLink: string): Observable<any> {
    return this.http.get(externalLink, {
      responseType: 'text',
      headers: { 'content-type': 'text/html' },
    });
  }

  acceptTerms(): Observable<any> {
    const user = this.currentUserValue;
    const v1baseUrl = environment.ssoBaseUrl.replace('/v2', '/v1');
    const url = `${v1baseUrl}/products/${this.productName}/accounts/${user.accountId}/users/me/terms`;
    return this.http.post<any>(url, { version: 1 }, defaultOptions).pipe(
      map((res) => {
        user.currentTermsOfUseAccepted = true;
        this.setCurrentUser(user);
      })
    );
  }
  resetUser() {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }
  loginWithCookie() {
    let userInfo;
    let skipCookieLogin = LOGIN_PATH_PREFIXES.some((prefix) =>
      location.pathname.startsWith(prefix)
    );
    if (
      localStorage.getItem('logoutInProgress') &&
      !location.href.includes('identity_provider=trimble_automation')
    ) {
      skipCookieLogin = true;
      localStorage.setItem('logoutInProgress', 'true');
    } else {
      localStorage.removeItem('logoutInProgress');
    }
    if (location.href.includes('identity_provider=trimble_automation')) {
      skipCookieLogin = false;
      const urlParams = new URLSearchParams(location.search);
      return this.http
        .post(
          `${environment.authwsUrl}v1/trimbleid/authenticate`,
          {
            authorizationCode: urlParams.get('code'),
            redirectUrl: location.origin + location.pathname,
            applicationName: this.productName,
            trimbleApplication: environment.tidApplicationName,
          },
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json; charset=UTF-8',
            }),
          }
        )
        .pipe(
          switchMap((user: any) => {
            userInfo = user;
            return this.getUserInfo(
              user.username,
              user.accountName,
              user.authToken
            ).pipe(
              map(([data, token]) => {
                data.user._jwtToken = token;
                this.setCurrentUser(data.user);
              })
            );
          }),
          catchError(() => {
            //while calling signup page from my account page.(expired user)
            if (
              !this.isPromoUser() &&
              !location.pathname.startsWith('/signup')
            ) {
              this.logout();
            }
            return of(null);
          })
        ) as Observable<unknown>;
    }
    if (skipCookieLogin) {
      this.resetUser();
      return of(null);
    }
    return this.http
      .post(
        `${environment.ssoBaseUrl}/security/verify`,
        {
          product: this.productName,
        },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json; charset=UTF-8',
          }),
          withCredentials: true,
        }
      )
      .pipe(
        switchMap((user: UserInfo) => {
          userInfo = user;
          return this.getUserInfo(
            user.userName,
            user.accountName,
            user.authToken
          ).pipe(
            map(([data, token]) => {
              data.user._jwtToken = token;
              this.setCurrentUser(data.user);
            })
          );
        }),
        catchError(() => {
          //while calling signup page from my account page.(expired user)
          if (!this.isPromoUser() && !location.pathname.startsWith('/signup')) {
            this.logout();
          }
          return of(null);
        })
      ) as Observable<unknown>;
  }

  logout(): void {
    // remove user from local storage to log user out
    if (this.refreshTokenSubscription) {
      this.refreshTokenSubscription.unsubscribe();
    }
    const authToken = this.currentUserValue?.authToken;
    localStorage.removeItem('currentUser');
    localStorage.removeItem('_ssoAccounts');
    if (authToken && !this.isPromoUser()) {
      if (sessionStorage) {
        sessionStorage.removeItem('routingProfileStore');
        sessionStorage.removeItem('searchView');
        sessionStorage.removeItem('reportDataVersion');
      }
      this.http
        .post<any>(`${environment.ssoBaseUrl}/security/logout`, {
          authToken: authToken,
        })
        .pipe(take(1))
        .subscribe(() => {
          localStorage.removeItem('currentApiKey');
          localStorage.removeItem('userAFSets');
          this.currentUserSubject.next(null);
          localStorage.setItem('logoutInProgress', 'true');
          this.router.navigate(['/tid/logout']);
        });
    }
  }
  /* getLicensedApps - gets the applications the currently logged in user can access
   * @memberof alkAuthenticationService
   */
  getLicensedApps(): Observable<WebToolsMenu[]> {
    const v1baseUrl = environment.ssoBaseUrl.replace('/v2', '/v1');
    return this.http.get<WebToolsMenu[]>(
      `${v1baseUrl}/users/${this.currentUserValue.authToken}/products`
    );
  }

  isTrialUser(user: UserInfo) {
    return user?.accountType === AccountType.TRIAL;
  }
  isTrialUserExpired(user: UserInfo) {
    return user?.expired;
  }
  reachedRouteLimit(user: UserInfo) {
    return user?.remainingRoutes <= 0 ? true : false;
  }

  isTrialInvalid(user: UserInfo) {
    return (
      this.isTrialUser(user) &&
      (this.isTrialUserExpired(user) ||
        this.reachedRouteLimit(user) ||
        checkTrialDateExpire(user))
    );
  }
  isPromoUser() {
    return this.currentUserValue?.account === environment.promo.account;
  }
}
